import Enums from '@core/types/api/Enums';

export enum PlanningProductSource {
  Catalog = 'catalog',
  New = 'new',
}

export const PLANNING_NODE_KIND_LABELS: Record<Enums.PlanningNodeKind, string> = {
  [Enums.PlanningNodeKind.Customer]: 'Customer',
  [Enums.PlanningNodeKind.Project]: 'Project',
  [Enums.PlanningNodeKind.Bucket]: 'Bucket',
};

// The design only words the customer line; the other two follow the domain's own definitions.
export const PLANNING_NODE_KIND_DESCRIPTIONS: Record<Enums.PlanningNodeKind, string> = {
  [Enums.PlanningNodeKind.Customer]: 'A named buyer with its own forecast.',
  [Enums.PlanningNodeKind.Project]: 'A project tracked on its own, apart from its customer.',
  [Enums.PlanningNodeKind.Bucket]:
    'Volume with no named buyer, such as smaller customers grouped together.',
};

export const PLANNING_PRODUCT_CLASSIFICATION_LABELS: Record<
  Enums.PlanningProductClassification,
  string
> = {
  [Enums.PlanningProductClassification.Aggregates]: 'Aggregates',
  [Enums.PlanningProductClassification.OtherIncome]: 'Other income',
  [Enums.PlanningProductClassification.Intercompany]: 'Intercompany',
};

export const PLANNING_ROLLUP_ALL_SCOPE = 'all';

export type PlanningRollupScope =
  | typeof PLANNING_ROLLUP_ALL_SCOPE
  | Enums.PlanningProductClassification;

export const PLANNING_ROLLUP_SCOPE_LABELS: Record<PlanningRollupScope, string> = {
  [PLANNING_ROLLUP_ALL_SCOPE]: 'All business',
  ...PLANNING_PRODUCT_CLASSIFICATION_LABELS,
};

export const PLANNING_ROLLUP_SCOPE_CAPTIONS: Record<PlanningRollupScope, string> = {
  [PLANNING_ROLLUP_ALL_SCOPE]: 'Combined across every classification, matching the total above',
  [Enums.PlanningProductClassification.Aggregates]: 'Counts toward aggregate totals and ASP',
  [Enums.PlanningProductClassification.Intercompany]: 'Excluded from aggregate totals and ASP',
  [Enums.PlanningProductClassification.OtherIncome]: 'Excluded from aggregate totals and ASP',
};

// In the design's order.
export const PLANNING_ROLLUP_SCOPES: PlanningRollupScope[] = [
  PLANNING_ROLLUP_ALL_SCOPE,
  Enums.PlanningProductClassification.Aggregates,
  Enums.PlanningProductClassification.Intercompany,
  Enums.PlanningProductClassification.OtherIncome,
];

export const PLANNING_ROLLUP_ASP_NOTE =
  'No ASP column: ASP is not additive, so the ASPs above cannot be summed. Every ASP below is ' +
  "derived from its own scope's summed tons and revenue. Corporate Demand is a top-down plug " +
  'shown separately; its tons are already inside Budget Tons.';

// The API cannot tell a division with no members from one whose members are all in markets the
// caller cannot see, so the copy holds for both.
export const PLANNING_EMPTY_DIVISION_TEXT =
  'No locations you can see are assigned to this division, so there is nothing to aggregate. ' +
  'This is not a division whose figures happen to be zero — assign a location with Edit and ' +
  'its numbers appear here.';

export const PLANNING_EMPTY_DIVISION_VIEWER_TEXT =
  'No locations you can see are assigned to this division, so there is nothing to aggregate. ' +
  'This is not a division whose figures happen to be zero — its numbers appear here once a ' +
  'Planning editor assigns a location.';

export const PLANNING_UNKNOWN_LOCATION = 'Unknown location';

// The API's own messages say "reporting group"; the page calls them divisions.
export const PLANNING_DIVISION_NAME_TAKEN = 'A division with this name already exists';
export const PLANNING_DIVISION_HIDDEN_MEMBERS =
  'This division has locations in markets you cannot access, so only someone with access to ' +
  'all of them can delete it.';

export const PLANNING_RESET_DIVISIONS_TITLE = 'Reset to default?';
export const PLANNING_RESET_DIVISIONS_TEXT =
  'Every division you added or renamed is discarded and every location goes back to its admin ' +
  'division. No planning data is affected.';

export const PLANNING_GRAND_TOTAL_LABEL = 'Company grand total';
export const PLANNING_UNGROUPED_ID = 'ungrouped';
export const PLANNING_UNGROUPED_LABEL = 'Ungrouped';
export const PLANNING_UNGROUPED_RESERVED = 'Ungrouped is kept for locations in no division';

export const PLANNING_PRODUCT_SOURCE_LABELS: Record<PlanningProductSource, string> = {
  [PlanningProductSource.Catalog]: 'From the catalog',
  [PlanningProductSource.New]: 'New Product',
};

export const NO_DIVISION_LABEL = 'No division';

export enum PlanningMonthState {
  Closed = 'closed',
  Current = 'current',
  Future = 'future',
}

// Where a month's forecast comes from, once the budget is approved.
export enum PlanningForecastSource {
  Budget = 'budget',
  Edited = 'edited',
  Empty = 'empty',
  Closed = 'closed',
}

export const PLANNING_MONTH_STATE_LABELS: Record<PlanningMonthState, string> = {
  [PlanningMonthState.Closed]: 'Closed month',
  [PlanningMonthState.Current]: 'Current month',
  [PlanningMonthState.Future]: 'Future month',
};

// Tons and ASP are stored as NUMERIC(14,2) and NUMERIC(12,2).
export const PLANNING_BUDGET_DECIMALS = 2;
export const PLANNING_TONS_MAX_INTEGER_DIGITS = 12;
export const PLANNING_ASP_MAX_INTEGER_DIGITS = 10;

// The Create fiscal year drawer's start months begin this far back, so the year in progress
// can still be created.
export const PLANNING_YEAR_START_MONTHS_BACK = 11;
export const PLANNING_YEAR_START_OPTIONS = 35;

export const PLANNING_EMPTY_VALUE = '—';
export const PLANNING_NO_TONS = 'N/A';
export const PLANNING_MIXED_ASP = 'Mixed';

// Month and range labels are part of the design's English copy.
export const PLANNING_COPY_LOCALE = 'en-US';
export const CENTS_PER_UNIT = 100;
// Tons are stored to the hundredth, so roll-ups sum whole hundredths, as money sums cents.
export const HUNDREDTHS_PER_TON = 10 ** PLANNING_BUDGET_DECIMALS;
// The ▲/▼ stepper's increment.
export const PLANNING_BUDGET_STEP = 1;

export enum PlanningGridColumn {
  Tons = 0,
  Asp = 1,
}

export enum PlanningGridMode {
  Edit = 'edit',
  ReadOnly = 'readOnly',
}

export const PLANNING_SELECT_PRODUCT_HINT = 'Select a product under a customer to edit';
export const PLANNING_SELECT_NODE_PRODUCT_HINT = 'Select a product to edit';
export const PLANNING_BANNER_SUBTITLE = 'Budget by location, customer and product';
export const PLANNING_CREATE_YEAR_LABEL = 'Create Fiscal Year';
export const PLANNING_APPROVE_BUDGET_TITLE = 'Approve budget?';
export const PLANNING_BUDGET_APPROVED_ERROR_KIND = 'planning_budget_approved';
export const PLANNING_APPROVED_WHILE_EDITING =
  "This budget was approved while you were editing. Your changes weren't saved.";
export const PLANNING_EXPORT_UNSAVED = 'Save or discard your changes to export.';
export const PLANNING_FORECAST_LABEL = 'Actual / Forecast';
export const PLANNING_AT_BUDGET_HINT = 'At budget, not yet revised';
export const PLANNING_REVERT_ROWS_LABEL = 'Revert Rows To Budget';
